import { useCallback, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import {
  ChevronDown as ChevronDownIcon,
  ChevronUp as ChevronUpIcon,
} from '@vgno/icons';

import { Badge } from '../Asset/shared/Badge/Badge';

import type { FC, ReactNode } from 'react';

import * as styles from './TimetableItem.module.scss';

interface TimetableItemProps {
  title: string;
  description?: ReactNode;
  time?: string;
  isCurrentlyLive?: boolean;
}

const TimetableItem: FC<TimetableItemProps> = ({
  title,
  description,
  time,
  isCurrentlyLive = false,
}) => {
  const [expanded, setExpanded] = useState(false);
  const { t } = useTranslation();

  const handleClick = useCallback(() => {
    setExpanded((prevExpanded) => !prevExpanded);
  }, []);

  const handleKeyPress = useCallback(
    (e) => {
      if (e.code === 'Enter' || e.code === 'Space') {
        handleClick();
      }
    },
    [handleClick]
  );

  return (
    <div
      className={classNames(styles.entry, {
        [styles.liveEntry]: isCurrentlyLive,
        [styles.expanded]: Boolean(description) && expanded,
      })}
      role="button"
      tabIndex={0}
      onKeyPress={handleKeyPress}
      onClick={handleClick}
      title={title}
    >
      <Badge
        label={isCurrentlyLive ? t('Now') : time}
        kind={isCurrentlyLive ? 'live' : 'normal'}
        className={classNames({
          [styles.stockBadge]: !isCurrentlyLive,
          [styles.liveBadge]: isCurrentlyLive,
        })}
      />
      <div className={styles.content}>
        <div className={styles.title}>{title}</div>
        {description && <div className={styles.description}>{description}</div>}
      </div>
      {description && (
        <div className={styles.expandIconWrapper}>
          {expanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
        </div>
      )}
    </div>
  );
};

export { TimetableItem };
